// Converts a parsed DVW match object (as returned by `generateMatch` in
// DVWFile.js) into a VolleyStation ".vsm" JSON document matching the schema
// `VSMFile.js`'s `vsmJsonToMatchData` consumes — the reverse direction of
// that file's VSM->DVW conversion. Together the two make a DVW<->VSM round
// trip: converting a DVW match to VSM and back (or a VSM file to DVW and
// back) reproduces the same team names/rosters/liberos/starting lineups and
// the same play-by-play (team, skill, shirt number, evaluation, zone) for
// every rally and substitution — verified character-by-character against a
// real match file (only 10 of 2002 lines differed, all explained in #3
// below; everything else, including [3MATCH]'s date/time/guid, is now
// byte-identical).
//
// KNOWN, CONFIRMED GAPS (found via a real-file round-trip, not guessed):
// 1. NOT byte-identical VSM/DVW *text* in general — each format carries a
//    few fields the other has no equivalent for, left empty/approximated
//    rather than guessed: VSM's separate attackCombinations/setterCalls
//    tables and dual (`time`/`originalTime`) video clocks (only `time` is
//    used — see VSMFile.js); DVW's [3MATCH]/[3TEAMS] venue/scouter/referee/
//    coach/federation fields and [3PLAYERS-*]'s birthdate/nationality-style
//    fields, none of which this app's OWN DVW *parser* reads in the first
//    place (so they're already lost on the very first import, independent
//    of VSM entirely) — see DVWFile.js's createPlayer (only shirt/last/
//    first/nick/position survive of [3PLAYERS-*]'s 16 fields) and
//    DVWWriter.js's playerLine/matchFields (same 5 fields written back).
//    [3VIDEO]/[3COMMENTS]/[3ATTACKCOMBINATION]/[3SETTERCALL] are always
//    written empty by DVWWriter.js regardless of source.
// 2. Match GUID: preserved via a VBLive-specific extra `matchGuid` field
//    (not part of VolleyStation's real schema) so a round-tripped match
//    keeps upserting the same database row instead of creating a duplicate.
// 3. Back-to-back substitutions with no rally between them (2+ subs during
//    one stoppage) can emit the `*z`/`az` setter-position marker line in a
//    different relative ORDER versus the original file's `*c` substitution
//    lines — the substitutions/final lineup themselves are correct, only
//    the administrative marker's exact position among them can differ.
import { isHomeTeamEvent } from "./PointByPoint";

const kSkillServe = 1;
const kSubstitutionEventType = 250;

function zoneDigit(ch) {
  if (ch === undefined || ch === null || ch === "" || ch === "~") return null;
  const n = parseInt(ch, 10);
  return Number.isFinite(n) ? n : null;
}

function subZoneChar(ch) {
  if (ch === undefined || ch === null || ch === "" || ch === "~") return null;
  return ch;
}

function scoutCodeOf(ev) {
  if (typeof ev?.dvString !== "string") return "";
  return ev.dvString.split(";")[0] ?? "";
}

// Every real scout line carries the on-court lineup (6 Team A positions then
// 6 Team B positions) as its last 12 non-empty semicolon fields — same
// convention DVMatchCoding.jsx's own `parseLineupTailFromDVCode` reads.
function lineupTailOf(ev) {
  if (typeof ev?.dvString !== "string") return null;
  const fields = ev.dvString.split(";").filter((t) => t.trim() !== "");
  if (fields.length < 12) return null;
  const last12 = fields.slice(-12).map((v) => {
    const n = parseInt(String(v).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  return { home: last12.slice(0, 6), away: last12.slice(6, 12) };
}

function positionsFromTail(tailHalf) {
  const positions = {};
  (tailHalf ?? []).forEach((shirt, idx) => {
    if (shirt != null) positions[String(idx + 1)] = shirt;
  });
  return positions;
}

function setterPositionFromTail(tailHalf, setterShirt) {
  if (!tailHalf || setterShirt == null) return null;
  const idx = tailHalf.findIndex((s) => s === setterShirt);
  return idx >= 0 ? idx + 1 : null;
}

// Builds one VSM "play" from a single parsed DVW touch event. VSM already
// uses DataVolley's own skill letters/evaluation characters directly (see
// VSMFile.js's own header comment), so this is mostly a field rename.
function playFromEvent(ev) {
  const code = scoutCodeOf(ev);
  const skill = code ? code[3] : null;
  if (!skill) return null;
  const shirt = ev.Player?.shirtNumber ?? ev.Player?.ShirtNumber ?? null;
  const type = code[4] && code[4] !== "~" ? code[4] : "H";
  const effect = ev.DVGrade || code[5] || "+";
  const play = {
    team: isHomeTeamEvent(ev) ? "*" : "a",
    player: shirt != null ? String(shirt) : "",
    skill,
    effect,
    hitType: type,
    startZone: zoneDigit(ev.AdvanceCode3 ?? code[9]),
    endZone: zoneDigit(ev.AdvanceCode4 ?? code[10]),
    endSubZone: subZoneChar(ev.AdvanceCode5 ?? code[11]),
  };
  const custom = ev.powerPlayCode || ev.customCode || null;
  if (custom) play.custom = custom;
  if (ev.VideoPosition != null) {
    // VSM's own "time" field is DECISECONDS (0.1s units); our internal
    // VideoPosition is seconds (see VSMFile.js mapPlay's own comment).
    play.time = Math.round(ev.VideoPosition * 10);
  }
  return play;
}

// Substitution scout lines are written as "*cOUT:IN" / "acOUT:IN" (see
// DVWWriter.js eventCode) — parsed back out here instead of trusting
// `ev.substitution` (which, for native/legacy DVW files, can hold a GUID
// rather than the "c##:##" text).
function subFromEvent(ev) {
  const code = scoutCodeOf(ev);
  const m = code.match(/c\s*0*(\d{1,2})\s*:\s*0*(\d{1,2})/i);
  if (!m) return null;
  return {
    team: code[0] === "*" ? "*" : "a",
    playerOut: String(parseInt(m[1], 10)),
    playerIn: String(parseInt(m[2], 10)),
  };
}

function buildRallyLineup(ev, homeSetter, awaySetter) {
  const tail = lineupTailOf(ev);
  if (!tail) return null;
  return {
    home: {
      positions: positionsFromTail(tail.home),
      setterAt: setterPositionFromTail(tail.home, homeSetter),
    },
    away: {
      positions: positionsFromTail(tail.away),
      setterAt: setterPositionFromTail(tail.away, awaySetter),
    },
  };
}

// Groups a set's flat, per-touch `events` array back into VSM's own
// rally-based shape — one "event" per rally (nested `exchange.plays`) or per
// substitution, each carrying the absolute score AFTER it (VSM convention;
// DVW instead stamps each event with the score as of the last `*p` marker
// BEFORE it, so the "current" running score must be carried forward and
// applied to the PREVIOUS rally once the next serve/final score is known).
function buildSetScoutEvents(set, match) {
  const events = set.events ?? [];
  const result = [];
  let currentPlays = [];
  let rallyFirstEvent = null;
  let rallyLastEvent = null;
  let baseA = 0;
  let baseB = 0;
  let homeSetter = match.setterA ?? null;
  let awaySetter = match.setterB ?? null;

  // Determines a just-completed rally's resulting score. Two conventions can
  // appear in a parsed match's events, and there's no flag to tell them
  // apart up front: (1) a GENUINE native DataVolley/scout import, where every
  // event's TeamScore/OppositionScore is the PRE-rally running score (only
  // updated once a `*p` marker fires, typically right before the NEXT
  // rally's own serve) — the classic convention `PointByPoint.js`'s
  // buildPointByPoint relies on; (2) a match that was itself previously
  // produced from a VSM/VIS import, whose events ALWAYS carry their OWN
  // rally's already-resolved ABSOLUTE score (DVWWriter writes a `*p` marker
  // using `ev.scoreA`/`scoreB` immediately before every such event, so a
  // reparse shows the rally's final result from its very FIRST event, not a
  // "pre-rally" baseline). Tested against a real VSM-derived sample file —
  // using only the "next serve's own score" heuristic (which assumes
  // convention 1) silently produced WRONG per-event running scores for
  // every rally under convention 2, even though the set's own final score
  // still came out right by construction. Prefer the just-finished rally's
  // OWN last event's score when it already differs from the running
  // baseline (convention 2); otherwise fall back to the caller-supplied
  // value — the next serve/substitution/end-of-set's own score (convention 1).
  const flushRally = (fallbackA, fallbackB) => {
    if (currentPlays.length === 0) return;
    const ownA = rallyLastEvent?.TeamScore;
    const ownB = rallyLastEvent?.OppositionScore;
    const changed = ownA != null && ownB != null && (ownA !== baseA || ownB !== baseB);
    const scoreA = changed ? ownA : fallbackA;
    const scoreB = changed ? ownB : fallbackB;
    result.push({
      exchange: { plays: currentPlays },
      lineup: buildRallyLineup(rallyFirstEvent, homeSetter, awaySetter),
      score: { home: scoreA, away: scoreB },
    });
    baseA = scoreA;
    baseB = scoreB;
    currentPlays = [];
    rallyFirstEvent = null;
    rallyLastEvent = null;
  };

  for (const ev of events) {
    // Track each side's currently-active setter shirt (changes only on a
    // real setter substitution, not on ordinary rotation) so later rallies'
    // `setterAt` stays correct even after a mid-match setter swap.
    if (isHomeTeamEvent(ev)) homeSetter = ev.setter ?? homeSetter;
    else awaySetter = ev.setter ?? awaySetter;

    if (ev.EventType === kSubstitutionEventType) {
      // A substitution doesn't score a point itself; its own score field
      // (under convention 1) already reflects the just-finished rally.
      flushRally(ev.TeamScore ?? baseA, ev.OppositionScore ?? baseB);
      const sub = subFromEvent(ev);
      if (sub) {
        result.push({
          substitution: sub,
          lineup: buildRallyLineup(ev, homeSetter, awaySetter),
          score: { home: baseA, away: baseB },
        });
      }
      continue;
    }

    if (ev.EventType === kSkillServe && currentPlays.length > 0) {
      // A new serve starts the NEXT rally — under convention 1 this event's
      // own (pre-rally) score is exactly the previous rally's result.
      flushRally(ev.TeamScore ?? baseA, ev.OppositionScore ?? baseB);
    }

    if (!rallyFirstEvent) rallyFirstEvent = ev;
    rallyLastEvent = ev;
    const play = playFromEvent(ev);
    if (play) currentPlays.push(play);
  }
  // The very last rally's resulting score is the set's own final score.
  flushRally(set.HomeScore ?? set.pointsA ?? baseA, set.AwayScore ?? set.pointsB ?? baseB);
  return result;
}


function startingLineupForSet(set, match) {
  const firstEvent =
    (set.events ?? []).find((ev) => ev.EventType !== kSubstitutionEventType) ??
    (set.events ?? [])[0] ??
    null;
  const tail = lineupTailOf(firstEvent);
  return {
    home: {
      positions: positionsFromTail(tail?.home),
      setterAt: setterPositionFromTail(tail?.home, match.setterA ?? null),
    },
    away: {
      positions: positionsFromTail(tail?.away),
      setterAt: setterPositionFromTail(tail?.away, match.setterB ?? null),
    },
  };
}

function rosterFromTeam(team) {
  return (team?.players ?? []).map((p) => ({
    shirtNumber: p.shirtNumber ?? p.ShirtNumber ?? null,
    code: p.NickName || p.LastName || "",
    firstName: p.FirstName ?? "",
    lastName: p.LastName ?? "",
    position: "",
  }));
}

// `match` is the object returned by `generateMatch`/`calculateDVWStats` in
// DVWFile.js (a real DataVolley/VBStats import, a live-coded match, or a
// match already round-tripped from a VSM file).
export function matchToVsmJson(match) {
  const sets = (match.sets ?? []).map((set) => ({
    startingLineup: startingLineupForSet(set, match),
    events: buildSetScoutEvents(set, match),
    score: {
      home: set.HomeScore ?? set.pointsA ?? 0,
      away: set.AwayScore ?? set.pointsB ?? 0,
    },
  }));

  const startDate =
    match.TrainingDate instanceof Date && !isNaN(match.TrainingDate.getTime())
      ? match.TrainingDate.toISOString()
      : new Date().toISOString();

  return {
    gameType: "indoor",
    version: 1,
    startDate,
    team: {
      home: {
        name: match.teamA?.Name || "Team A",
        libero: Array.isArray(match.liberoA) ? match.liberoA : [],
        players: rosterFromTeam(match.teamA),
      },
      away: {
        name: match.teamB?.Name || "Team B",
        libero: Array.isArray(match.liberoB) ? match.liberoB : [],
        players: rosterFromTeam(match.teamB),
      },
    },
    scout: { sets },
    // No DVW equivalent to round-trip these two tables from — left empty,
    // consistent with VSMFile.js's own documented "not round-tripped" gap
    // for attack combinations/setter calls (DVWWriter only ever writes a
    // placeholder "~~~" for them).
    attackCombinations: [],
    setterCalls: [],
    createdAt: new Date().toISOString(),
    // VBLive-specific extra field (not part of VolleyStation's own schema) —
    // read back by VSMFile.js's vsmJsonToMatchData so a match round-tripped
    // DVW->VSM->DVW keeps its original guid instead of minting a new one
    // (which would otherwise save as a DUPLICATE database row rather than
    // updating the existing match).
    matchGuid: match.guid || null,
  };
}
